import express, { Application, IRouterHandler, RequestHandler } from "express";
import type { IRouterMatcher, ParamsDictionary, RequestHandlerParams } from "express-serve-static-core";
import { ParsedQs } from "qs";

import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { LoyaltyValidation } from "@smartrr/shared/interfaces/loyalty/validation";
import { QuickbuyAPI } from "@smartrr/shared/interfaces/quickbuy/api";

import { apiPrefixedHost, customerPortalRoutePrefix } from "@smartrr/shared/constants";
import { FailedBill } from "../entities/Billing";
import { ICancellationReason, IUserCancellation } from "../entities/CancellationRelationship";
import { ICustomerPortalTheme } from "../entities/CustomerPortalTheme";
import { ICustomerRelationship } from "../entities/CustomerRelationship";
import { ICreateGift, IGift } from "../entities/Gift";
import { ISODateString } from "../entities/ISODateString";
import { IMailingAddress, IMailingAddressCreate, IMailingAddressUpsert } from "../entities/MailingAddress";
import { ISmartrrOrderFromDb } from "../entities/Order";
import { IOrganization } from "../entities/Organization";
import { PayoutMethodType } from "../entities/PayoutMethod";
import { IPurchasablePaginatedResponse } from "../entities/Purchasable";
import { IPurchaseState, ISubscriptionProperties } from "../entities/PurchaseState";
import { ISmartrrBundleConfig, ISmartrrSellingPlanGroup } from "../entities/SellingPlanGroup";
import { ISmartrrCountry } from "../interfaces/Country";
import { IStripeWalletIntent } from "../interfaces/Stripe";
import {
  FailureRes,
  Req,
  ReqMiddleware,
  SuccessRes,
  TypedBodyRequestInit,
  TypedBodyRequestNoReqBodyInit,
} from "../req";
import { SellingPlanFragmentFragment, SubscriptionDraftInput } from "../shopifyGraphQL/api";
import { IShopifyLiquidCustomer } from "../shopifyLiquid/customer";
import { ICustomerPortalValidationObj } from "../shopifyLiquid/validation";
import { IShopifyCollection } from "../utils/bundles";
import { PaginationResult } from "../utils/paginatedQuery";
import { CountryCode } from "@smartrr/shared/countryCode";

interface WithData<Type> {
  data: Type[];
  count: number;
}

//
// route definitions
//

interface GetRoutes extends NoReqBodyRoutesInterfaceGuarantee {
  "/": NoReqBodyReqRes<string, {}, ICustomerPortalValidationObj & ParsedQs>;

  "/purchasable": NoReqBodyReqRes<IPurchasablePaginatedResponse, {}>;

  "/orders": NoReqBodyReqRes<PaginationResult<ISmartrrOrderFromDb>, {}>;

  "/addresses": NoReqBodyReqRes<{ addresses: IMailingAddress[] }, {}>;

  "/integrations/stripe/publishable": NoReqBodyReqRes<{ publicKey: string }>;

  "/purchase-state": NoReqBodyReqRes<IPurchaseState[], {}>;

  "/customer-portal-theme": NoReqBodyReqRes<ICustomerPortalTheme | null, {}>;

  "/countries/data": NoReqBodyReqRes<ISmartrrCountry[]>;

  "/cancellation-reasons": NoReqBodyReqRes<ICancellationReason[], {}>;

  "/selling-plan-group": NoReqBodyReqRes<
    ISmartrrSellingPlanGroup[],
    {},
    {
      cache?: "true" | "false";
    }
  >;
  "/auth/session": NoReqBodyReqRes<
    {
      customerRelationship: ICustomerRelationship;
      organization: IOrganization;
    },
    {}
  >;
  "/bundle-config": NoReqBodyReqRes<
    {
      configs: ISmartrrBundleConfig[];
      collections: IShopifyCollection[];
    },
    {}
  >;
  "/get-all-configs": NoReqBodyReqRes<
    {
      sellingPlanGroups: ISmartrrSellingPlanGroup[];
      addOnConfigs: ISmartrrSellingPlanGroup[];
      trendingConfigs: ISmartrrSellingPlanGroup[];
    },
    {},
    {
      cache?: "true" | "false";
      enablement?: "true" | "false";
    }
  >;
  "/bills/:pmtMthId/failed-bills": NoReqBodyReqRes<
    FailedBill[],
    {
      pmtMthId: string;
    }
  >;
  "/loyalty": IReqRes<void, LoyaltyApi.Program.Type | null>;
  "/loyalty/referral-link": IReqRes<void, LoyaltyApi.ReferralCode.Type | null>;

  "/quick-buy/products": IReqRes<
    void,
    WithData<QuickbuyAPI.Product.Type>,
    { limit: string; offset: string; productName?: string | undefined }
  >;
}

interface PostRoutes extends RoutesInterfaceGuarantee {
  // auth
  "/auth/session": IReqRes<
    {
      liquidCustomer?: IShopifyLiquidCustomer;
    },
    {
      customerRelationship: ICustomerRelationship;
      organization: IOrganization;
    },
    {}
  >;

  "/customerRelationship/:customerRelationshipId/card-wallet/:payoutMethodType": NoReqBodyReqRes<
    IStripeWalletIntent,
    {
      customerRelationshipId: string;
      payoutMethodType: PayoutMethodType.stripe;
    }
  >;

  "/customerRelationship/:customerRelationshipId/update-shopify-payment-method": IReqRes<
    { paymentMethodId: string },
    null,
    { customerRelationshipId: string }
  >;

  //
  // non-sequential purchase state post routes
  //

  "/purchase-state/:id/gift/:skdIdx": IReqRes<
    Omit<ICreateGift, "stId" | "skdIdx">,
    IGift,
    { id: string; skdIdx: string }
  >;

  "/address": IReqRes<
    {
      newMailingAddress: IMailingAddressCreate;
      isDefault: boolean;
    },
    {
      createdAddress: IMailingAddress;
      isDefault: boolean;
    }
  >;

  "/purchase-state": IReqRes<
    {
      startDate: ISODateString;
      sellingPlan: SellingPlanFragmentFragment;
      items: { variantId: string; quantity: number }[];
    },
    IPurchaseState
  >;

  "/user-cancellation": IReqRes<IUserCancellation, IUserCancellation>;

  "/bundle-config/:bundleId": IReqRes<ISmartrrBundleConfig, { bundleId: string }>;

  "/loyalty/redeem/reward-product/:variantId/purchase-state/:cpsId": IReqRes<
    {},
    {
      orgPoints?: number;
      updatedCPS?: IPurchaseState;
    },
    LoyaltyValidation.Redeem.Variant.Type
  >;
  "/loyalty/redeem/incentive/:incentiveId/purchase-state/:cpsId": IReqRes<
    {},
    {
      orgPoints?: number;
      updatedCPS?: IPurchaseState;
    },
    LoyaltyValidation.Redeem.Incentive.Type
  >;
}

interface PutRoutes extends RoutesInterfaceGuarantee {
  "/purchase-state/:id/next-billing-date": IReqRes<
    {
      nextShipmentDate: ISODateString;
    },
    IPurchaseState,
    { id: string }
  >;

  "/purchase-state/:id/send-now": IReqRes<{ date: ISODateString }, IPurchaseState, { id: string }>;

  "/purchase-state/:id/skip": IReqRes<{ date: ISODateString }, IPurchaseState, { id: string }>;

  "/purchase-state/:id/unskip": IReqRes<{ date: ISODateString }, IPurchaseState, { id: string }>;

  "/default-address/:addressId": IReqRes<
    { updatedMailingAddress: IMailingAddressUpsert },
    { defaultAddress: IMailingAddress },
    { addressId: string }
  >;

  "/address/:addressId": IReqRes<
    { updatedMailingAddress: IMailingAddressUpsert },
    { updatedAddress: IMailingAddress },
    { addressId: string }
  >;
}

interface DeleteRoutes extends NoReqBodyRoutesInterfaceGuarantee {
  "/purchase-state/:id": NoReqBodyReqRes<IPurchaseState, { id: string }>;

  "/address/:addressId": NoReqBodyReqRes<{ deletedAddressId: string }, { addressId: string }>;

  "/purchase-state/:id/gift/:skdIdx": NoReqBodyReqRes<{}, { id: string; skdIdx: string }>;
}

export class TypedFrontendCustomerApi extends Req {
  constructor(middlewares: ReqMiddleware[]) {
    super(`https://${apiPrefixedHost}${customerPortalRoutePrefix}`, {}, middlewares);
  }

  getReq<Path extends keyof ISmartrCustomerPortalRoutes["get"] & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestNoReqBodyInit<
      ISmartrCustomerPortalRoutes["get"][Path]["Params"],
      ISmartrCustomerPortalRoutes["get"][Path]["Query"]
    > = {}
  ) {
    return super.getReq<
      ISmartrCustomerPortalRoutes["get"][Path]["ResBody"],
      ISmartrCustomerPortalRoutes["get"][Path]["Params"],
      ISmartrCustomerPortalRoutes["get"][Path]["Query"]
    >(endpoint, requestConfig);
  }

  putReq<Path extends keyof ISmartrCustomerPortalRoutes["put"] & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestInit<
      ISmartrCustomerPortalRoutes["put"][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes["put"][Path]["Params"],
      ISmartrCustomerPortalRoutes["put"][Path]["Query"]
    > = {}
  ) {
    return super.putReq<
      ISmartrCustomerPortalRoutes["put"][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes["put"][Path]["ResBody"],
      ISmartrCustomerPortalRoutes["put"][Path]["Params"],
      ISmartrCustomerPortalRoutes["put"][Path]["Query"]
    >(endpoint, requestConfig);
  }

  postReq<Path extends keyof ISmartrCustomerPortalRoutes["post"] & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestInit<
      ISmartrCustomerPortalRoutes["post"][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes["post"][Path]["Params"],
      ISmartrCustomerPortalRoutes["post"][Path]["Query"]
    > = {}
  ) {
    return super.postReq<
      ISmartrCustomerPortalRoutes["post"][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes["post"][Path]["ResBody"],
      ISmartrCustomerPortalRoutes["post"][Path]["Params"],
      ISmartrCustomerPortalRoutes["post"][Path]["Query"]
    >(endpoint, requestConfig);
  }

  deleteReq<Path extends keyof ISmartrCustomerPortalRoutes["delete"] & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestNoReqBodyInit<
      ISmartrCustomerPortalRoutes["delete"][Path]["Params"],
      ISmartrCustomerPortalRoutes["delete"][Path]["Query"]
    > = {}
  ) {
    return super.deleteReq<
      ISmartrCustomerPortalRoutes["delete"][Path]["ResBody"],
      ISmartrCustomerPortalRoutes["delete"][Path]["Params"],
      ISmartrCustomerPortalRoutes["delete"][Path]["Query"]
    >(endpoint, requestConfig);
  }
}

//
// sequential purchase state post routes
//
interface SequentialPurchaseStatePostRoutes extends RoutesInterfaceGuarantee {
  "/purchase-state/:id/line-item": IReqRes<
    {
      variantId: string;
      quantity: number;
      sellingPlanId: string;
      skdIdx?: number;
      countryCode?: CountryCode;
    },
    IPurchaseState,
    {
      id: string;
    }
  >;

  "/purchase-state/:id/discount": IReqRes<{ redeemCode: string }, IPurchaseState, { id: string }>;

  "/purchase-state/:id/line-item/:id/swap": IReqRes<
    {
      variantId: string;
      quantity: number;
      sellingPlanId: string;
      skdIdx?: number;
      countryCode?: CountryCode;
    },
    IPurchaseState,
    { id: string; lineItemId: string }
  >;
}

interface SequentialPurchaseStatePutRoutes extends RoutesInterfaceGuarantee {
  "/purchase-state/:id/line-item/:lineItemId": IReqRes<
    {
      variantId?: string;
      quantity?: number;
      sellingPlanId?: string;
      countryCode?: CountryCode;
    },
    IPurchaseState,
    { id: string; lineItemId: string }
  >;

  "/purchase-state/:id/selling-plan": IReqRes<
    {
      sellingPlanId: string;
    },
    IPurchaseState,
    { id: string }
  >;

  "/purchase-state/:id/cancel": NoReqBodyReqRes<{ newCustomerPurchaseState: IPurchaseState }, { id: string }>;

  "/purchase-state/:id/pause": IReqRes<
    {
      unpauseDate: ISODateString;
    },
    IPurchaseState,
    { id: string }
  >;

  "/purchase-state/:id/activate": NoReqBodyReqRes<IPurchaseState, { id: string }>;

  "/purchase-state/:id/line-item/:lineItemId/remove": NoReqBodyReqRes<
    IPurchaseState,
    { id: string; lineItemId: string }
  >;

  // update subscription properties (address, etc)
  // we could also opt to make more specific routes
  // for certain props but for now this is fine
  "/purchase-state/:id": IReqRes<
    {
      subscriptionProperties: ISubscriptionProperties;
      input: SubscriptionDraftInput;
    },
    IPurchaseState,
    { id: string }
  >;
}

type SequentialPurchaseStateDeleteRoutes = RoutesInterfaceGuarantee;

/*
 * This class is used exclusively for routes that change properties of a CustomerPurchaseState,
 * such as quantity. Subsequent requests to the same CPS are appended to a promise chain so that
 * earlier requests can finish updating the CPS. When a previous request is completed, the queue
 * attempts to update the CPS id of the next request with the result of the previous one in the
 * case where the CPS is immutable and a new one is created and returned from a property update.
 */

type SequentialRoutes = SequentialPurchaseStatePostRoutes &
  SequentialPurchaseStatePutRoutes &
  SequentialPurchaseStateDeleteRoutes;

export class TypedFrontendCustomerPurchaseStateApi extends TypedFrontendCustomerApi {
  private requestQueues: {
    [customerPurchaseStateId: string]: Promise<SuccessRes<IPurchaseState> | FailureRes>;
  } = {};

  private makeRequest<Path extends keyof SequentialRoutes & string>(
    endpoint: Path,
    method: "post" | "put" | "delete",
    requestConfig: TypedBodyRequestInit<
      SequentialRoutes[Path]["ReqBody"],
      SequentialRoutes[Path]["Params"],
      SequentialRoutes[Path]["Query"]
    > = {},
    nextId?: string
  ) {
    const requestConfigToUse = nextId
      ? {
          ...requestConfig,
          params: { ...requestConfig.params, id: nextId },
        }
      : requestConfig;

    switch (method) {
      case "post": {
        return super.postReq<Path>(endpoint, requestConfigToUse);
      }
      case "put": {
        return super.putReq<Path>(endpoint, requestConfigToUse);
      }
      case "delete": {
        return super.deleteReq<Path>(endpoint, requestConfigToUse);
      }
      // No default
    }
  }

  private addToQueue<Path extends keyof SequentialRoutes & string>(
    endpoint: Path,
    method: "post" | "put" | "delete",
    requestConfig: TypedBodyRequestInit<
      SequentialRoutes[Path]["ReqBody"],
      SequentialRoutes[Path]["Params"],
      SequentialRoutes[Path]["Query"]
    > = {}
  ) {
    // default ID as we want to block for all ops, some are tied to specific CPSs
    const id: string | undefined = ((requestConfig.params || {}) as any).id;
    if (id === undefined) {
      throw new Error("customerPurchaseState id not supplied to request");
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (!this.requestQueues[id]) {
      this.requestQueues[id] = this.makeRequest(endpoint, method, requestConfig);
      return this.requestQueues[id];
    }

    this.requestQueues[id] = this.requestQueues[id].then(res => {
      if (res.type === "success") {
        const customerPurchaseState = res.body;
        const nextId = customerPurchaseState.id;

        // move pointer along to next ID for incoming requests
        this.requestQueues[nextId] = this.requestQueues[id];
        delete this.requestQueues[id];

        return this.makeRequest(endpoint, method, requestConfig, nextId);
      }
      return res;
    });

    return this.requestQueues[id];
  }

  // @ts-ignore
  postReq<Path extends keyof SequentialPurchaseStatePostRoutes & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestInit<
      SequentialPurchaseStatePostRoutes[Path]["ReqBody"],
      SequentialPurchaseStatePostRoutes[Path]["Params"],
      SequentialPurchaseStatePostRoutes[Path]["Query"]
    > = {}
  ) {
    return this.addToQueue(endpoint, "post", requestConfig);
  }

  // @ts-ignore
  putReq<Path extends keyof SequentialPurchaseStatePutRoutes & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestInit<
      SequentialPurchaseStatePutRoutes[Path]["ReqBody"],
      SequentialPurchaseStatePutRoutes[Path]["Params"],
      SequentialPurchaseStatePutRoutes[Path]["Query"]
    > = {}
  ) {
    return this.addToQueue(endpoint, "put", requestConfig);
  }

  // @ts-ignore
  deleteReq<Path extends keyof SequentialPurchaseStateDeleteRoutes & string>(
    endpoint: Path,
    requestConfig: TypedBodyRequestInit<
      SequentialPurchaseStateDeleteRoutes[Path]["ReqBody"],
      SequentialPurchaseStateDeleteRoutes[Path]["Params"],
      SequentialPurchaseStateDeleteRoutes[Path]["Query"]
    > = {}
  ) {
    return this.addToQueue(endpoint, "delete", requestConfig);
  }
}

//
// Consumption of typed routes for back end (Express) begins here
//

interface CombinedPostRoute extends PostRoutes, SequentialPurchaseStatePostRoutes {}

interface CombinedPutRoute extends PutRoutes, SequentialPurchaseStatePutRoutes {}

interface CombinedDeleteRoute extends DeleteRoutes, SequentialPurchaseStateDeleteRoutes {}

interface ISmartrCustomerPortalRoutes {
  get: { [K in keyof GetRoutes]: GetRoutes[K] };
  post: { [K in keyof CombinedPostRoute]: CombinedPostRoute[K] };
  put: { [K in keyof CombinedPutRoute]: CombinedPutRoute[K] };
  delete: { [K in keyof CombinedDeleteRoute]: CombinedDeleteRoute[K] };
}

export type HTMLRes = string;

interface IReqRes<
  ReqBody extends {} | void,
  ResBody extends {} | HTMLRes | null | void = void,
  ReqParams extends ParamsDictionary = {},
  ReqQuery extends ParsedQs = {},
> extends BaseReqRes {
  Params: ReqParams;
  ReqBody: ReqBody;
  ResBody: ResBody;
  Query: ReqQuery;
}

type NoReqBodyReqRes<
  ResBody extends {} | null | void = void,
  ReqParams extends ParamsDictionary = {},
  ReqQuery extends ParsedQs = {},
> = IReqRes<void, ResBody, ReqParams, ReqQuery>;

// TODO: figure out how to add type arguments here so that the list of routes can be guaranteed
// right now a route not in this list has type "any" for request and response
interface RoutesInterfaceGuarantee {
  // to have full guarantee, get rid of this
  [key: string]: BaseReqRes;
}
interface NoReqBodyRoutesInterfaceGuarantee {
  // to have full guarantee, get rid of this
  [key: string]: BaseReqResNoBody;
}

interface BaseReqRes {
  Params: ParamsDictionary;
  ReqBody: any;
  ResBody: any;
  Query: any;
}

interface BaseReqResNoBody extends BaseReqRes {
  Params: ParamsDictionary;
  ReqBody: void;
  ResBody: any;
  Query: any;
}

interface ISmartrrRouterMatcher<T, Method extends keyof ISmartrCustomerPortalRoutes> {
  <Path extends keyof ISmartrCustomerPortalRoutes[Method]>(
    path: Path,
    ...handlers: RequestHandler<
      ISmartrCustomerPortalRoutes[Method][Path]["Params"],
      ISmartrCustomerPortalRoutes[Method][Path]["ResBody"],
      ISmartrCustomerPortalRoutes[Method][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes[Method][Path]["Query"]
    >[]
  ): T;

  <Path extends keyof ISmartrCustomerPortalRoutes[Method]>(
    path: Path,
    ...handlers: RequestHandlerParams<
      ISmartrCustomerPortalRoutes[Method][Path]["Params"],
      ISmartrCustomerPortalRoutes[Method][Path]["ResBody"],
      ISmartrCustomerPortalRoutes[Method][Path]["ReqBody"],
      ISmartrCustomerPortalRoutes[Method][Path]["Query"]
    >[]
  ): T;

  <Path extends keyof ISmartrCustomerPortalRoutes[Method]>(path: Path, subApplication: Application): T;
}

// typed express router
export interface ISmartrrCustomerApiExpressRouter extends express.Express {
  get: ISmartrrRouterMatcher<this, "get">;
  post: ISmartrrRouterMatcher<this, "post">;
  put: ISmartrrRouterMatcher<this, "put">;
  delete: ISmartrrRouterMatcher<this, "delete">;
  use: IRouterHandler<this> & IRouterMatcher<this>;
}
